import { useEffect, useReducer } from 'react'

import { customFetch } from '@agos/api-client'

import { DEFAULT_LANGUAGE } from './language-catalog'
import type {
  TranslationDictionaryResponse,
  TranslationsFilled,
} from '@agos/api-client/model'

/**
 * The translation memory, ported from legacy `lib/i18n/translation-loader.ts`.
 *
 * Slovak is the source language: `tt("Uložiť")` returns its argument unchanged
 * while the UI is in Slovak, so the Slovak strings live in the code and stay
 * byte-for-byte what legacy shows (FRONTEND.md §8, §14.2). Any other language
 * is served from a dictionary the API fills in — first in bulk, then lazily for
 * strings the UI reaches that the memory has never seen.
 *
 * Deliberately a module-level singleton rather than React state: `tt` is called
 * from constant tables at module scope (status labels, type labels) long before
 * any component mounts, exactly as legacy does. `useTT()` is what re-renders a
 * component when the dictionary grows.
 */

const MISSING_FLUSH_DELAY_MS = 500
const MISSING_BATCH_SIZE = 100
const TRANSLATION_NAMESPACE = 'manager'

let dictionary: Record<string, string> = {}
let currentLanguage: string = DEFAULT_LANGUAGE

const subscribers = new Set<() => void>()
/** Strings seen but not yet translated, waiting for the next flush. */
const pendingSources = new Set<string>()
/** Strings already asked about, so a re-render does not re-queue them. */
const requestedSources = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

function notifySubscribers(): void {
  for (const subscriber of subscribers) subscriber()
}

export function getCurrentLanguage(): string {
  return currentLanguage
}

export function setTranslationLanguage(language: string): void {
  if (language === currentLanguage) return
  currentLanguage = language
  dictionary = {}
  requestedSources.clear()
  notifySubscribers()
  if (language !== DEFAULT_LANGUAGE) void loadDictionary()
}

async function loadDictionary(): Promise<void> {
  try {
    const response = await customFetch<Partial<TranslationDictionaryResponse>>(
      `/api/v1/translations/dict?lang=${encodeURIComponent(currentLanguage)}`,
    )
    dictionary = response?.dict ?? {}
    notifySubscribers()
  } catch {
    // A missing dictionary shows Slovak, which every member of staff reads.
    // Failing loudly here would break the whole shell over a translation.
  }
}

/**
 * Translate synchronously.
 *
 * Returns the source string on a miss and schedules a background fetch, so the
 * screen renders in Slovak now and switches when the answer arrives — never a
 * blank or a key.
 */
export function tt(text: string | null | undefined): string {
  if (!text) return ''
  if (currentLanguage === DEFAULT_LANGUAGE) return text

  const hit = dictionary[text]
  if (hit !== undefined) return hit

  if (!requestedSources.has(text)) {
    requestedSources.add(text)
    pendingSources.add(text)
    scheduleFlush()
  }
  return text
}

function scheduleFlush(): void {
  if (flushTimer !== null) return
  flushTimer = setTimeout(() => void flushPendingSources(), MISSING_FLUSH_DELAY_MS)
}

async function flushPendingSources(): Promise<void> {
  flushTimer = null
  if (currentLanguage === DEFAULT_LANGUAGE || pendingSources.size === 0) return

  const batch = [...pendingSources].slice(0, MISSING_BATCH_SIZE)
  for (const source of batch) pendingSources.delete(source)

  try {
    const response = await customFetch<Partial<TranslationsFilled>>(
      '/api/v1/translations/missing',
      {
        method: 'POST',
        body: JSON.stringify({
          lang: currentLanguage,
          texts: batch,
          namespace: TRANSLATION_NAMESPACE,
        }),
      },
    )
    if (response?.dict) {
      Object.assign(dictionary, response.dict)
      for (const source of Object.keys(response.dict)) requestedSources.delete(source)
      notifySubscribers()
    }
  } catch {
    // Forget that these were asked about, so the next render retries them.
    for (const source of batch) requestedSources.delete(source)
  }

  if (pendingSources.size > 0) scheduleFlush()
}

/** Subscribe a component to dictionary growth. */
export function useTT(): (text: string | null | undefined) => string {
  const [, forceRender] = useReducer((tick: number) => tick + 1, 0)

  useEffect(() => {
    subscribers.add(forceRender)
    return () => {
      subscribers.delete(forceRender)
    }
  }, [])

  return tt
}
